import constants from '@/constants'
import api from '@/api'
import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersistence from 'vuex-persist'
import createLogger from 'vuex/dist/logger'

Vue.use(Vuex)

function actionLoggerPlugin (store) {
  store.subscribeAction(({ type, payload }, state) => {
    console.debug(`Before dispatching "${type}" with ${JSON.stringify(payload)}`)
  })
}

const vuexLocal = new VuexPersistence({
  // use localStorage to save certain store states to the user's browser
  storage: window.localStorage,
  // key to store the states under
  key: 'knowmak.eu' + (process.env.NODE_ENV !== 'production' ? '-' + process.env.NODE_ENV : ''),
  // pick specific store states to persist
  reducer: state => ({
    showTour: state.showTour,
    showStartDialog: state.showStartDialog,
    appV: state.appV
  })
})

const vuexSession = new VuexPersistence({
  // use localStorage to save certain store states to the user's browser
  storage: window.sessionStorage,
  // key to store the states under
  key: 'knowmak.eu',
  // pick specific store states to persist
  reducer: state => ({
    ssky: state.ssky
  })
})

// possible values for process.env.NODE_ENV: development, staging, production

export default new Vuex.Store({
  strict: process.env.NODE_ENV === 'development',
  // logging is only available for devs,
  // session keys are only saved to session storage on production
  plugins: (process.env.NODE_ENV === 'development' && process.env.ACTIVATE_LOGGERS)
    ? [createLogger(), actionLoggerPlugin, vuexLocal.plugin]
    : (process.env.NODE_ENV === 'production')
      ? [vuexLocal.plugin, vuexSession.plugin]
      : [vuexLocal.plugin],
  state: {
    ssky: 0, // session key
    unavail: constants.UNAVAIL, // string to display to users for unavailable data
    knowledgeTypes: [],
    profileIndicators: [],
    queryArchive: [],
    DTO: {
      group_by: ['country'],
      filter_by: [],
      data: [
        {
          country: 'None',
          value: 1
        }
      ]
    },
    data: [],
    profile: {},
    sipsMinimal: [],
    sipsFull: [],
    publicOrgsMinimal: {},
    publicOrgsFull: [],
    activePublicOrgsCategory: {},
    firmsMinimal: {},
    firmsFull: [],
    activeFirmsCategory: {},
    factSheetItemsHydrated: [], // store state shared by SIP and actors
    countryProfiles: [], // not used?
    regionProfiles: [], // not used?
    topicProfiles: [], // not used?
    countries: [],
    regions: [],
    topics: [],
    subtopics: [],
    years: [],
    indicators: [],
    allCountriesRegions: [], // maps all countries to all regions and vice versa
    selectedYear: {},
    selectedYearSpan: {start: {}, end: {}},
    selectedDimension: {},
    selectedIndicator: {},
    selectedNormalization: {},
    // TODO: Find a proper way to call normalizations from the api via
    // api.getNormalization()
    normalizations: constants.NORMALIZATION_CATEGORIES,
    selectedComplement: {},
    selectedComplementItem: {},
    selectedVisualization: 'activate-map',
    selectedIndicatorSet: [],
    geoMap: new Map(),
    topicMap: new Map(),
    indicatorMap: new Map(),
    serverError: false,
    badRequest: false,
    dataLoading: false,
    updateFiltersTimestamp: null,
    showFactSheet: false,
    showTour: true,
    showStartDialog: false,
    appV: 0 // used for publicly displayed version information
  },
  getters: {
    profileIndicatorsMeta: state => {
      const finishedInitializing = state.profileIndicators.length > 0 && state.indicators.length > 0 && state.knowledgeTypes.length > 0
      if (!finishedInitializing) {
        return []
      }

      let result = []

      state.profileIndicators.forEach(pi => {
        let ind = state.indicators.find(n => n.code === pi.indicator_id) // match indicator
        if (ind) {
          let ktype = state.knowledgeTypes.find(k => k.id.toLowerCase() === ind.knowledgeType) // match knowledge type
          result.push({
            id: pi.indicator_id,
            indicator_name: ind.text,
            indicator_description: ind.description,
            ktype_name: ktype.label
          })
        }
      })
      return result
    },
    extendedFactSheetData: state => {
      let obj = {
        grouping: [],
        year: state.profile.year,
        country: null,
        region: null,
        topic: null
      }
      let grouping = [state.selectedDimension.dimensionName]
      // get main dimension from 'code' key in payload / profile data
      obj[grouping[0]] = [state.profile.code]
      // also group by complement if filtering by complement is activated (each selected or individual selection)
      if (state.selectedComplementItem.value !== 'all') {
        let filterby = []
        if (state.selectedComplementItem.hasOwnProperty('code')) {
          // get complement code when one single complement is selected in dropdown
          filterby.push(state.selectedComplementItem.code)
        } else if (state.profile.complementItem.hasOwnProperty('code')) {
          // get individual complement code from payload / profile data when dropdown is set to "each selected"
          filterby.push(state.profile.complementItem.code)
        }

        if (filterby.length > 0) {
          grouping.push(state.selectedComplement.dimensionName)
          obj[grouping[1]] = filterby
        }
      }
      obj.grouping = grouping

      return obj
    },
    defaultActorsCategory: state => { // applicable to actors (public orgs, firms)
      return state.profileIndicators.find(i => i.default === true)
    },
    getCountryRegions (state, country) {
      return state.regions.filter(r => r.country === country.code)
    },
    /**
     * Return all (selected) items that belong to the requested dimension.
     *
     * @param dimension       requested dimension object
     * @param selectedOnly    if true elements are filtered by the attribute "selected"
     * @param selectionValue  determines the target value of the attribute "selected"
     */
    getDimensionItems: (state) => (dimension, selectedOnly = false, selectionValue = true) => {
      let result = []
      switch (dimension) {
        case constants.COUNTRY_DIMENSION:
          result = state.countries
          break
        case constants.REGION_DIMENSION:
          result = state.regions
          break
        case constants.CLASS_DIMENSION:
          result = state.topics
          break
        case constants.SUBCLASS_DIMENSION:
          result = state.subtopics
          break
        default:
          break
      }

      if (selectedOnly) {
        result = result.filter(r => r.selected === selectionValue)
      }

      return result
    },
    getDTO (state) {
      let getIndicatorAndNorm = state.data.some(d => d.indicatorName === state.selectedIndicator.text && d.normalization === state.selectedNormalization)
      if (Object.keys(state.selectedIndicator).length > 0 && getIndicatorAndNorm) {
        return state.data.find(d => d.indicatorName === state.selectedIndicator.text && d.normalization === state.selectedNormalization).DTO
      }
      // TODO - we are not setting an action here if the above condition is not met
      // what does that mean? what are we returning? an empty DTO?
      return state.DTO
    },
    getCountryFromRegionCode: (state, getters) => (code) => {
      let countryCode = state.regions.find(r => r.code === code).country
      return state.countries.find(c => c.code === countryCode).text
    }
  },
  mutations: {
    // all mutuations must be synchronous!
    initKnowledgeTypes (state, arr) {
      state.knowledgeTypes = arr
    },
    initProfileIndicators (state, arr) {
      state.profileIndicators = arr
    },
    initPublicOrgsMinimal (state) {
      let cats = state.profileIndicators.map(a => a.indicator_id)
      let actorsMinimal = {}
      for (const cat of cats) {
        actorsMinimal[cat] = []
      }
      state.publicOrgsMinimal = actorsMinimal
    },
    initFirmsMinimal (state) {
      let cats = state.profileIndicators.map(a => a.indicator_id)
      let actorsMinimal = {}
      for (const cat of cats) {
        actorsMinimal[cat] = []
      }
      state.firmsMinimal = actorsMinimal
    },
    setSessionKey (state, val) {
      state.ssky = val
    },
    setYears (state, years) {
      state.years = years
    },
    setCountries (state, countries) {
      state.countries = countries
    },
    setRegions (state, regions) {
      state.regions = regions
    },
    setCountriesRegionsMatch (state, value) {
      state.allCountriesRegions = value
    },
    setTopics (state, topics) {
      state.topics = topics
    },
    setSubtopics (state, subtopics) {
      state.subtopics = subtopics
    },
    setIndicators (state, indicators) {
      // indicators for the indicator dropdown (includes "fake" ones for indicator groups)
      state.indicators = indicators
    },
    //  ▼▼▼ ---- these seem to be unused store mutations? ---- ▼▼▼
    setCountryProfiles (state, countryProfiles) {
      state.countryProfiles = countryProfiles
    },
    setRegionProfiles (state, regionProfiles) {
      state.regionProfiles = regionProfiles
    },
    setTopicProfiles (state, topicProfiles) {
      state.topicProfiles = topicProfiles
    },
    // ▲▲▲ ---- these seem to be unused store mutations?  ---- ▲▲▲
    addData (state, payload) {
      state.data.push({DTO: payload.DTO, indicatorName: payload.indicatorName, normalization: payload.normalization})
    },
    setData (state, data) {
      state.data = data
    },
    // setDTO (state, DTO) {
    //   state.data = [{DTO: DTO, indicatorName: state.selectedIndicator.text}]
    // },
    setSelectedYear (state, selectedYear) {
      state.selectedYear = selectedYear
    },
    setSelectedYearSpan (state, selectedYearSpan) {
      state.selectedYearSpan = selectedYearSpan
    },
    setSelectedDimension (state, selectedDimension) {
      state.selectedDimension = selectedDimension
    },
    setSelectedComplement (state, selectedComplement) {
      state.selectedComplement = selectedComplement
    },
    setSelectedComplementItem (state, selectedComplementItem) {
      state.selectedComplementItem = selectedComplementItem
    },
    setSelectedIndicator (state, selectedIndicator) {
      state.selectedIndicator = selectedIndicator
    },
    setSelectedNormalization (state, selectedNormalization) {
      state.selectedNormalization = selectedNormalization
    },
    setSelectedVisualization (state, selectedVisualization) {
      state.selectedVisualization = selectedVisualization
    },
    setGeoMap (state, entries) {
      entries.forEach(function (d) {
        state.geoMap.set(d.code, d.text)
      })
    },
    setTopicMap (state, entries) {
      entries.forEach(function (d) {
        state.topicMap.set(d.code, d)
      })
    },
    setIndicatorMap (state, entries) {
      // store codes and names of all possible indicators along with "extended" codes
      // using normalisations for keeping track of what to download via the download dialogue?
      entries.forEach(function (d) {
        // state.indicatorMap.set(d.code, d.text)
        state.indicatorMap.set(d.code, d.text)
      })
    },
    setBadRequest (state, badRequest) {
      state.badRequest = badRequest
    },
    setServerError (state, serverError) {
      state.serverError = serverError
    },
    setDataLoading (state, dataLoading) {
      state.dataLoading = dataLoading
    },
    setUpdateFiltersTimestamp (state, timestamp) {
      state.updateFiltersTimestamp = timestamp
    },
    setTourState (state, showTour) {
      state.showTour = showTour
    },
    setStartDialog (state, bool) {
      state.showStartDialog = bool
    },
    setPublicAppVersion (state, int) {
      state.appV = int
    },
    setSelectedIndicatorSet (state, selectedIndicatorSet) {
      state.selectedindicatorSet = selectedIndicatorSet
    },
    addNormalization (state, normalization) {
      state.normalizations.push(normalization)
    },
    setShowFactSheet (state, showFactSheet) {
      state.showFactSheet = showFactSheet
    },
    setProfile (state, profile) {
      state.profile = profile
    },
    setSipsMinimal (state, arr) {
      state.sipsMinimal = arr
    },
    setSipsFull (state, arr) {
      state.sipsFull = arr
    },
    setPublicOrgsMinimal (state, payload) {
      state.publicOrgsMinimal[payload.actorsCategory] = payload.actors
    },
    addPublicOrgsFull (state, arr) {
      state.publicOrgsFull.push(arr)
    },
    resetPublicOrgsFull (state) {
      state.publicOrgsFull = []
    },
    setActivePublicOrgsCategory (state, obj) {
      state.activePublicOrgsCategory = obj
    },
    setFirmsMinimal (state, payload) {
      state.firmsMinimal[payload.actorsCategory] = payload.actors
    },
    addFirmsFull (state, arr) {
      state.firmsFull.push(arr)
    },
    resetFirmsFull (state) {
      state.firmsFull = []
    },
    setActiveFirmsCategory (state, obj) {
      state.activeFirmsCategory = obj
    },
    addFactSheetItemsHydrated (state, arr) {
      // use same store state for SIP and actors
      state.factSheetItemsHydrated.push(...arr)
    },
    resetFactSheetItemsHydrated (state) {
      state.factSheetItemsHydrated = []
    }
  },
  actions: {
    toggleStartDialog ({ commit }, bool) {
      commit('setStartDialog', bool)
    },
    initAppVersion ({ state, commit }, int) {
      if (state.appV !== int) {
        commit('setPublicAppVersion', int)
        commit('setStartDialog', true)
      }
    },
    async loadPublicOrgs ({commit, dispatch}, payload) {
      let actorType = 'publicOrgs'
      let endpointType = 'public_orgs_endpoint'
      let endpointDetails = constants.API_PROFILES_PUBLIC_ORG + '/' + 'detail'
      let publicOrgsCat = payload.id

      let publicOrgs = await dispatch('loadActorsMinimal', {actorType: actorType, actorsCategory: publicOrgsCat, endpointType: endpointType})
      commit('setPublicOrgsMinimal', {actorsCategory: publicOrgsCat, actors: publicOrgs})

      // after basic info is loaded, query API again for detailed info
      // TODO refactor to avoid duplication of queries
      let publicOrgsIds = publicOrgs.map(a => a.actorId)
      if (publicOrgsIds.length > 0) {
        let publicOrgsFull = await dispatch('loadActorsFull', {actorType: actorType, ids: publicOrgsIds, endpoint: endpointDetails})
        commit('addPublicOrgsFull', publicOrgsFull)
        commit('addFactSheetItemsHydrated', publicOrgsFull)
      }
    },
    async loadFirms ({commit, dispatch}, payload) {
      let actorType = 'firms'
      let endpointType = 'firms_endpoint'
      let endpointDetails = constants.API_PROFILES_FIRMS + '/' + 'detail'
      let firmsCat = payload.id

      let firms = await dispatch('loadActorsMinimal', {actorType: actorType, actorsCategory: firmsCat, endpointType: endpointType})
      commit('setFirmsMinimal', {actorsCategory: firmsCat, actors: firms})

      // after basic info is loaded, query API again for detailed info
      // TODO refactor to avoid duplication of queries
      let firmsIds = firms.map(a => a.actorId)
      if (firmsIds.length > 0) {
        let firmsFull = await dispatch('loadActorsFull', {actorType: actorType, ids: firmsIds, endpoint: endpointDetails})
        commit('addFirmsFull', firmsFull)
        commit('addFactSheetItemsHydrated', firmsFull)
      }
    },
    // load fully hydrated objects for actors = public orgs, firms
    async loadActorsFull ({state, commit}, payload) {
      let actorType = payload.actorType
      let actorIds = payload.ids
      let apiEndpoint = payload.endpoint

      return api.getActorsFull(state.ssky, apiEndpoint, actorIds)
        .then((response) => {
          if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
            console.debug(actorType + ' fully hydrated: ' + decodeURIComponent(response.url))
          }
          // add new key extendedId so no differentiation between SIP and actors extended items is necessary
          return response.body.data.map(item => Object.assign({}, item, {
            extendedId: item.actor_id,
            label: item.actor_name, // TODO have changed to label in API?!
            labelAlt: item.actor_name_en
          }))
        })
        // endpoint cannot be reached or returns invalid data
        .catch((response) => {
          return []
        })
    },
    // basic list of actors (public orgs or firms) for the current selection (dimension, year, complement)
    // actorCategory refers to the indicator for which data is displayed in fact sheets (vertical slider)
    async loadActorsMinimal ({state, getters, commit, dispatch}, payload) {
      let profileData = getters.extendedFactSheetData
      let actorType = payload.actorType
      let actorsCategory = payload.actorsCategory
      let apiEndpoint = (state.profileIndicators.find(a => a.indicator_id === actorsCategory))[payload.endpointType]

      let grouping = profileData.grouping
      if (!profileData.grouping.includes('year')) {
        grouping.push('year')
      }

      return api.getActorsMinimal(state.ssky, apiEndpoint, grouping, profileData.year, profileData.country, profileData.region, profileData.topic)
        .then((response) => {
          if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
            console.debug(actorType + ' minimal info: ' + decodeURIComponent(response.url))
          }
          let actorsRaw = response.body.data
          let actors = actorsRaw.map(a => ({
            actorId: a.actor_id,
            label: a.actor_name, // TODO have changed to label in API?!
            labelAlt: a.actor_name_en, // TODO see above
            value: a.value,
            rank: a.rank
          }))
          actors.sort((a, b) => {
            if (a.rank > b.rank) {
              return 1
            }
            if (a.rank < b.rank) {
              return -1
            }
            if (a.rank === b.rank) {
              // sort actors with identical rank alphabetically, using English locale
              let labelA = a.label
              let labelB = b.label
              return labelA.localeCompare(labelB, 'en', {sensitivity: 'base'})
            }
          })
          return actors
        })
        // endpoint cannot be reached or returns invalid data
        .catch((response) => {
          return []
        })
    },
    // load fully hydrated SIP objects
    loadSipsFull: function ({state, commit}) {
      let sipIds = state.sipsMinimal.map(s => s.sip_id)
      let sipDetails = api.getSipsFull(state.ssky, sipIds)
      sipDetails
        .then((response) => {
          if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
            console.debug('SIPs fully hydrated: ' + decodeURIComponent(response.url))
          }
          // add new key extendedId so no differentiation between SIP and Actors extended items is necessary
          let items = response.body.data.map(item => Object.assign({}, item, {
            extendedId: item.sip_id,
            label: item.title // TODO have changed to label in API?!
          }))
          commit('setSipsFull', items)
          commit('addFactSheetItemsHydrated', items)
        })
        // endpoint cannot be reached or returns invalid data
        // TODO refactor commits to incorporate erraneous responses
        .catch((response) => {
        })
    },
    // list of Social Innovation Projects for the current selection (dimension, complement)
    loadSipsMinimal: function ({state, getters, commit, dispatch}, payload = {}) {
      let profileData = getters.extendedFactSheetData
      let grouping = profileData.grouping
      if (grouping.includes('year')) {
        let index = grouping.indexOf('year')
        if (index > -1) {
          grouping.splice(index, 1)
        }
      }

      let sipData = api.getSipsMinimal(state.ssky, grouping, profileData.country, profileData.region, profileData.topic)
      sipData
        .then((response) => {
          if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
            console.debug('SIPs minimal info: ' + decodeURIComponent(response.url))
          }
          let sipsRaw = response.body.data
          let sips = sipsRaw.map(s => ({
            sip_id: s.sip_id,
            label: s.title // TODO have changed to label in API?!
          }))
          sips.sort(function (a, b) {
            return a.label.localeCompare(b.label)
          })
          commit('setSipsMinimal', sips) // load basic info for currently relevant SIPs
          dispatch('loadSipsFull') // after basic info is loaded, query API again for detailed info
        })
        // endpoint cannot be reached or returns invalid data
        // TODO refactor commits to incorporate erraneous responses
        .catch((response) => {
        })
    },
    // initially load or reset profile data = data used in (basic) fact sheets
    loadProfile ({state, getters, commit}, payload = {}) {
      let code = payload.code
      let dimension = state.selectedDimension
      let complement = state.selectedComplement
      let complementItem = state.selectedComplementItem
      if (state.selectedComplementItem === constants.EACH_SELECTED_COMPLEMENT) {
        // switch profiles to the individual item that gets clicked on when "each selected" is active
        // first load of "each selected" (pre-click) remains at "aggregate all"
        if (payload.complement) {
          let getDimensionItems = this.getters.getDimensionItems(state.selectedComplement, true)
          complementItem = getDimensionItems.filter(c => c.code === payload.complement)[0]
        } else {
          complementItem = constants.AGGREGATE_COMPLEMENTS
        }
      }
      let filterByComplement = complementItem !== constants.AGGREGATE_COMPLEMENTS && Object.keys(complementItem).length > 0
      let that = this
      let profileData = {}
      switch (state.selectedDimension) {
        case constants.COUNTRY_DIMENSION:
          profileData = api.getCountryProfile(state.ssky, code, state.selectedYear.year, complement, complementItem, filterByComplement)
          break
        case constants.REGION_DIMENSION:
          profileData = api.getRegionProfile(state.ssky, code, state.selectedYear.year, complement, complementItem, filterByComplement)
          break
        case constants.CLASS_DIMENSION:
          profileData = api.getTopicProfile(state.ssky, code, state.selectedYear.year, complement, complementItem, filterByComplement)
          break
        case constants.SUBCLASS_DIMENSION:
          profileData = api.getTopicProfile(state.ssky, code, state.selectedYear.year, complement, complementItem, filterByComplement)
          break
        default:
          break
      }
      profileData.then((response) => {
        if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
          console.debug('profile data: ' + decodeURIComponent(response.url))
        }
        let profile = response.body
        if (dimension === constants.CLASS_DIMENSION || dimension === constants.SUBCLASS_DIMENSION) {
          profile['class'] = code
        }
        profile = Object.assign(profile, {code: code, complement: complement, complementItem: complementItem})
        that.commit('setProfile', profile)
      })
    },
    initSessionKey ({state, commit}) {
      // session IDs are only to be requested in production
      if (process.env.NODE_ENV === 'production' && (state.ssky === 0 || typeof state.ssky === 'undefined')) {
        api.getSessionKey().then(response => {
          let sessKey = response.data.id
          this.commit('setSessionKey', sessKey)
        })
      } else {
        // output info on mode and session key on staging and development versions
        console.debug('env: ' + process.env.NODE_ENV + ', sess: ' + state.ssky)
      }
    },
    loadIndicatorData ({state, dispatch}, payload = {}) {
      let indicator = {}
      let normalization = {}

      if (!payload.hasOwnProperty('indicator')) {
        indicator = state.selectedIndicator
      } else {
        indicator = payload.indicator
      }

      if (!payload.hasOwnProperty('normalization')) {
        normalization = state.selectedNormalization
      } else {
        normalization = payload.normalization
      }

      if (!state.data.some(d => d.indicatorName === indicator.text && d.normalization === normalization)) {
        let payload = {
          indicator: indicator,
          normalization: normalization
        }
        this.dispatch('loadData', payload)
      }

      /*
      // TODO: ADD NORMALIZATION FEATURE
      state.indicators.filter(i => i.selected).forEach(i => {
        if (!state.data.some(d => d.indicatorName === i.text) && state.selectedIndicator !== i) {
          this.dispatch('loadData', {indicator: i}) // TODO: ADD NORMALIZATION FEATURE
        }
      })
      */
    },
    refreshIndicatorData ({commit, dispatch}) {
      this.commit('setData', [])
      this.dispatch('loadIndicatorData')
    },
    selectDimension ({ state, dispatch, commit }, dimension, loadData = true) {
      this.commit('setSelectedDimension', dimension)

      if (state.selectedDimension.dimensionType === state.selectedComplement.dimensionType) {
        this.commit('setSelectedComplement', constants.DIMENSIONS.find(c => c.dimensionType !== state.selectedDimension.dimensionType))
        this.commit('setSelectedComplementItem', constants.AGGREGATE_COMPLEMENTS)
      }

      if (state.selectedVisualization === 'activate-map' && state.selectedDimension.dimensionType === 'topic') {
        this.commit('setSelectedVisualization', 'activate-barchart')
      }

      if (loadData) {
        this.dispatch('refreshIndicatorData')
      }
    },
    selectComplement ({ state, dispatch, commit }, complement, loadData = true) {
      this.commit('setSelectedComplementItem', constants.AGGREGATE_COMPLEMENTS)
      this.commit('setSelectedComplement', complement)
      if (loadData) {
        this.dispatch('refreshIndicatorData')
      }
    },
    // triggered whenever the second dimension is changed - either by changing filters
    // or by switching between display modes in the "Show" bar (show total vs. each selected vs. individual dimensions)
    selectComplementItem ({ state, dispatch, commit }, payload) {
      this.commit('setSelectedComplementItem', payload.complementItem)
      if (payload.complementItem === constants.EACH_SELECTED_COMPLEMENT && state.selectedVisualization === 'activate-map') {
        this.commit('setSelectedVisualization', 'activate-barchart')
      }
      if (payload.loadData) {
        this.dispatch('refreshIndicatorData')
      }
      // check if it's necessary to run this every time the complement changes, esp.
      // seeing as we are currently not actually (re)loading an open fact sheet with the new data
      // -> maybe this can be run lazily / only when the user requests a new fact sheet
      if (Object.keys(state.profile).length > 0) {
        this.dispatch('loadProfile', {code: state.profile.code})
      }
    },
    selectIndicator ({ state, dispatch, commit }, payload) {
      let indicator = payload.selectedIndicator
      let loadData = payload.hasOwnProperty('loadData') ? payload.loadData : true

      this.commit('setSelectedIndicator', indicator)
      if (indicator.invalidDimensionTypes.includes(state.selectedDimension.dimensionType)) {
        let validDimension = constants.DIMENSIONS.find(d => d.dimensionType !== state.selectedDimension.dimensionType)
        this.dispatch('selectDimension', validDimension, false) // TODO: think of potential concurrency problems, is the payload correctly passed?
      }

      let resetNormalization = false
      if (Array.isArray(indicator.norm_by)) {
        if (state.selectedNormalization !== constants.NO_NORM &&
          !indicator.norm_by.some(n => n.name === state.selectedNormalization.name)) {
          resetNormalization = true
        }
      } else {
        resetNormalization = true
      }
      if (resetNormalization) {
        this.dispatch('selectNormalization', {selectedNormalization: constants.NO_NORM, loadData: false})
      }

      if (loadData) {
        this.dispatch('loadIndicatorData')
      }
      // if (!state.selectedIndicatorSet.includes(indicator.text)) {
      //   state.selectedIndicatorSet.push(indicator.text)
      // }
      //
      // this.dispatch('selectIndicatorSet', {selectedIndicatorSet: state.selectedIndicatorSet, loadData: loadData})
    },
    selectNormalization ({ state, dispatch, commit }, payload) {
      let normalization = payload.selectedNormalization
      let loadData = payload.hasOwnProperty('loadData') ? payload.loadData : true

      this.commit('setSelectedNormalization', normalization)

      if (loadData) {
        this.dispatch('loadIndicatorData')
      }
    },
    selectYear ({ dispatch, commit, state }, year) {
      this.commit('setSelectedYear', year)
      let yearSpan = Object.assign({}, state.selectedYearSpan)
      if (yearSpan.start.year !== year.year) {
        yearSpan.start = year
      }
      if (yearSpan.end.year < year.year) {
        yearSpan.end = year
      }
      this.commit('setSelectedYearSpan', yearSpan)
      this.dispatch('loadProfile', {code: state.profile.code})
      // this.dispatch('loadData')
    },
    selectYearSpan ({ dispatch, commit }, yearSpan) {
      this.commit('setSelectedYearSpan', yearSpan)
    },
    selectVisualization ({state, commit}, selectedVisualization, loadData = true) {
      this.commit('setSelectedVisualization', selectedVisualization)
      if (selectedVisualization === 'activate-map' && state.selectedComplementItem === constants.EACH_SELECTED_COMPLEMENT) {
        this.dispatch('selectComplementItem', constants.AGGREGATE_COMPLEMENTS, loadData)
      }
    },
    async fetchKnowledgeTypes () {
      return constants.KNOWLEDGE_TYPES
    },
    async fetchProfileIndicators () {
      return constants.PROFILE_INDICATORS
    },
    initIndicators ({ commit, state }) {
      // add default normalization (not transported via indicators)
      // TODO: Changed with api v, remove after the update
      // Removed right now, check if there are any issues
      // this.commit('addNormalization', constants.NO_NORM)

      api.getIndicators().then(response => {
        let indicators = response.data.map(function (d) {
          let normBy = []
          // let normCategories = d.norm_by
          // let norm_obj = []
          // api.getNormalization().then(norm_response => { return norm_response.json()}).then(data => {norm_obj = data})
          // alert("norm_obj " + JSON.stringify(norm_obj) )
          if (Array.isArray(d.norm_by)) {
            // Extract available normalizations from indicators
            // and match them with the available normalization categories.
            // Add id and label of the matched categories as an object of
            // key and name properties into 'normBy'.
            normBy.push(
              d.norm_by.map(normCat => {
                // TODO: Get normalization categories from the api instead.
                // E.g. api.getNormalization
                let normInd = state.normalizations.findIndex(n => n.id === normCat)
                return {
                  id: state.normalizations[normInd].id,
                  value: state.normalizations[normInd].label
                }
              })
            )
            normBy = normBy[0]
            // TODO: Seems like it is not necessary anymore, check it
            // Make sure 'not normalised is always the first element
            if (normBy[0].id !== constants.NO_NORM.id) {
              let nonInd = normBy.findIndex(n => n.id === constants.NO_NORM.id)
              let nonObj = normBy.splice(nonInd, 1)[0]
              normBy.unshift(nonObj)
            }
          }
          // let patentWorkaround = {
          //   'indicators/patents/num_patent_app': 'indicators/patents/num_pat_app',
          //   'indicators/patents/num_patent_app_transnat': 'indicators/patents/num_pat_app_transnat'
          // }

          return {
            text: d.indicator_label, // human readable name of indicator
            code: d.indicator_id,
            active: d.active === true, // only active indicators are made available in the visualisation
            knowledgeType: d.knowledge_type,
            // TODO: This one needs to be fixed on the api side
            // URI path of patents defined in the profile indicators does not match with the actual URI
            // uri: (d.URI === 'indicators/patents/num_patent_app' || d.URI === 'indicators/patents/num_patent_app_transnat') ? patentWorkaround[d.URI] : d.URI,
            uri: d.URI,
            description: d.description,
            // rankGroup: d.rank1,
            // rankIndicator: d.rank2,
            invalidDimensionTypes: [], // dimensions which cannot be used with the indicator
            selected: false,
            norm_by: normBy
          }
        }, this)

        // TODO replace with values provided by API once invalidDimensionTypes are implemented for new indicators
        indicators.map(function (i) {
          if (i.code === 'population' || i.code === 'com_inx_volm' || i.code === 'com_inx_intsy') {
            if (i.invalidDimensionTypes.length === 0) {
              i.invalidDimensionTypes = ['topic']
            } else {
              console.debug('update invalid dimension types')
            }
          }
          return i
        })

        // indicators = indicators.filter(i => i.active).sort(function (a, b) {
        //   return a.rankGroup - b.rankGroup || a.rankIndicator - b.rankIndicator
        // })
        // this.commit('setIndicatorMap', indicators)

        // create "fake" indicators for indicator groups – needed for group names to get included in dropdowns
        let indicatorsWithHeaders = []
        let indicatorSections = indicators.map(i => i.knowledgeType)

        indicatorSections.forEach((i, pos) => {
          if (indicatorSections.indexOf(i) === pos) {
            const indicator = constants.KNOWLEDGE_TYPES.find(n => n.id === i)
            if (indicator) {
              indicatorsWithHeaders.push({header: indicator.label})
            }
          }
          indicatorsWithHeaders.push(indicators[pos])
        })

        this.commit('setIndicators', indicatorsWithHeaders)
        // TODO: this seems odd to me...
        // note KK 2019-07 - we shouldn't be loading initial data here but only in initSession
        // also can we use indicators[0] here? or is there a possibility for the indicators array to be empty?
        this.dispatch('selectIndicator', {selectedIndicator: indicators.find(i => true), loadData: true})
      })
    },
    initCountries ({ commit, state }) {
      api.getCountries().then(response => {
        let countries = response.data.map(function (d) {
          return {
            text: d.country_name,
            code: d.country_code,
            selected: true
          }
        })
        countries.sort(function (a, b) { return a.text.localeCompare(b.text) })

        this.commit('setCountries', countries)
        this.commit('setGeoMap', countries)
      })
    },
    initCountriesRegions ({ commit, state }) {
      return api.getCountriesRegions().then(response => {
        let match = response.data.map(function (d) {
          return {
            country_code: d.country_code,
            region_code: d.region
          }
        })
        this.commit('setCountriesRegionsMatch', match)
      })
    },
    initRegions ({ dispatch, commit, state }) {
      return this.dispatch('initCountriesRegions').then(() => {
        api.getCountriesRegions().then(response => {
          let match = response.data.map(function (d) {
            return {
              country_code: d.country_code,
              region_code: d.region
            }
          })
          this.commit('setCountriesRegionsMatch', match)
        }).then(
          api.getRegions().then(response => {
            let regions = response.data.map(function (d) {
              let countryCode = state.allCountriesRegions.find(function (m) {
                return m.region_code === d.adapted_nuts_code
              }).country_code
              return {
                text: d.adapted_nuts_name,
                code: d.adapted_nuts_code,
                country: countryCode,
                selected: true
              }
            }, this)

            regions.sort(function (a, b) {
              return a.text.localeCompare(b.text)
            })

            this.commit('setRegions', regions)
            this.commit('setGeoMap', regions)
          })
        )
      })
    },
    initTopics ({ commit, state }) {
      // load all the topics
      api.getTopics().then(response => {
        let topics = response.data.map(function (d) {
          return {
            text: d.label,
            code: d.class,
            parent: '',
            selected: true
          }
        })

        // load the ontology
        api.getOntology().then(response => {
          // get all KET & SGC descendants
          let ontology = response.data
          let rootTopicKeys = constants.ROOT_TOPICS.map(t => t.code)

          let ontologyKetSgc = ontology.filter(t => (rootTopicKeys.includes(t.class) && t.relative_depth === 1))

          let primaryTopics = []
          let subtopics = []

          // set parents for primary topics
          ontologyKetSgc.forEach(function (t) {
            let topic = topics.find(x => x.code === t.subclass)
            topic.parent = t.class
            primaryTopics.push(topic)
          })

          primaryTopics.sort(function (a, b) { return a.text.localeCompare(b.text) })

          // iterate all primary topics, find all descendants of each primary topic and set their parents
          primaryTopics.forEach(function (t) {
            // find all descendants of a single primary topics
            let topicOntology = ontology.filter(x => x.class === t.code && x.relative_depth === 1)

            topicOntology.forEach(function (x) {
              let topic = Object.assign({}, topics.find(z => z.code === x.subclass))
              topic.parent = t.code
              subtopics.push(topic)
            })
          })

          subtopics.sort(function (a, b) { return a.text.localeCompare(b.text) })

          this.commit('setTopics', primaryTopics)
          this.commit('setSubtopics', subtopics)

          this.commit('setTopicMap', constants.ROOT_TOPICS.concat(primaryTopics).concat(subtopics))
        })
      })
    },
    initYears ({ commit, state }) {
      api.getYears().then(response => {
        let years = response.data.map(function (d) {
          return {
            year: d.year,
            alert: d.alert === true,
            default: d.default === true,
            message: d.message
          }
        })
        // TODO: Is it unnecessary?
        //  years.sort(function (a, b) {
        //    return a.year > b.year
        //  })

        this.commit('setYears', years)

        this.commit('setSelectedYear', state.years.find(y => y.default))
        this.commit('setSelectedYearSpan', {start: state.selectedYear, end: state.selectedYear})
      })
    },
    async initSelection ({ commit, dispatch, state, getters }) {
      // initialise knowledge types
      const knowledgeTypes = dispatch('fetchKnowledgeTypes')
      const profileIndicators = dispatch('fetchProfileIndicators')

      // 0. Normalization
      // default is no normalization
      this.commit('setSelectedNormalization', constants.NO_NORM)

      // 1. Indicators
      this.dispatch('initIndicators')

      // 2. Countries
      this.dispatch('initCountries')

      // 3. Regions
      this.dispatch('initRegions')

      // 4. Topics
      this.dispatch('initTopics')

      // 5. Years
      this.dispatch('initYears')

      commit('initKnowledgeTypes', await knowledgeTypes)
      commit('initProfileIndicators', await profileIndicators)
      commit('initPublicOrgsMinimal', await profileIndicators)
      commit('initFirmsMinimal', await profileIndicators)
      commit('setActivePublicOrgsCategory', getters.defaultActorsCategory)
      commit('setActiveFirmsCategory', getters.defaultActorsCategory)
    },
    updateFilters ({ commit, state }, timestamp) {
      this.commit('setUpdateFiltersTimestamp', timestamp)
      setTimeout(() => {
        if (timestamp === state.updateFiltersTimestamp) {
          // TODO: reset the data
          // this.dispatch('loadData')
          this.dispatch('refreshIndicatorData')
          this.commit('setUpdateFiltersTimestamp', null)
        }
      }, 3000)
    },
    loadData ({ commit, state }, payload) {
      if (!payload.hasOwnProperty('indicator') || Object.keys(payload.indicator).length === 0) {
        payload.indicator = state.selectedIndicator
      }
      if (!payload.hasOwnProperty('normalization')) {
        payload.normalization = state.selectedNormalization
      }

      if (Object.keys(payload.indicator).length > 0) {
        this.commit('setDataLoading', true)

        let filterby = ''
        let normby = ''
        /*
        let filterby = '&year='
        for (var i = state.selectedYearSpan.start.year; i < state.selectedYearSpan.end.year; i++) {
          filterby += i + 'i'
        }
        filterby += state.selectedYearSpan.end.year
        */

        // if (this.getters.getAllDimensionItems.some(i => !i.selected) || state.selectedDimension.dimensionType === 'topic') {
        if (this.getters.getDimensionItems(state.selectedDimension).some(i => !i.selected) || state.selectedDimension.dimensionType === 'topic') {
          filterby += '&' + state.selectedDimension.dimensionName + '=' + this.getters.getDimensionItems(state.selectedDimension, true).map(i => i.code).join(',')
        }

        if (state.selectedComplementItem.hasOwnProperty('code')) {
          filterby += '&' + state.selectedComplement.dimensionName + '=' + state.selectedComplementItem.code
        } else if ((state.selectedComplementItem.value === 'selected' || state.selectedComplementItem.value === 'each') &&
          (this.getters.getDimensionItems(state.selectedComplement).some(i => !i.selected) || state.selectedComplement.dimensionType === 'topic')) {
          filterby += '&' + state.selectedComplement.dimensionName + '=' + this.getters.getDimensionItems(state.selectedComplement, true).map(i => i.code).join(',')
        }

        let groupBy = [state.selectedDimension.dimensionName]
        if (state.selectedComplementItem.value === 'all') {
          groupBy.push('year')
        } else if (state.selectedComplementItem.value === 'each') {
          groupBy.push(state.selectedComplement.dimensionName, 'year')
        } else {
          groupBy.push('year', state.selectedComplement.dimensionName)
        }

        // TODO: Following ones needs to be adjusted according to the instances of the normalization array
        if (payload.normalization.id !== constants.NO_NORM.id) {
          normby = '&normby=' + payload.normalization.id
        }

        let uriParts = [Vue.http.options.root, payload.indicator.uri]
        let uri
        if (!payload.indicator.uri.startsWith('https')) {
          uri = uriParts.join('/') + '?groupby=' + groupBy.join(',') + filterby + normby + '&session=' + state.ssky
        } else {
          // TODO remove once indicator URIs are not full paths anymore
          uri = payload.indicator.uri + '?groupby=' + groupBy.join(',') + filterby + normby + '&session=' + state.ssky
        }

        // for debugging - only available in development mode
        if (['development', 'dev_server'].includes(process.env.NODE_ENV)) {
          console.debug(uri)
        }

        let DTO = {}
        Vue.http.get(uri).then(response => {
          DTO = response.data
        }, response => {
          // error callback
          if (response.status >= 500) {
            this.commit('setServerError', true)
          } else {
            this.commit('setBadRequest', true)
          }

          DTO.data = []
          DTO.group_by = groupBy
        }).then(() => {
          // this.commit('setDTO', DTO)
          this.commit('addData', {indicatorName: payload.indicator.text, normalization: payload.normalization, DTO: DTO})
          this.commit('setDataLoading', false)
        })
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/vuex/store.js